import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useOpenFromQuery } from '@/hooks/useOpenFromQuery';
import { Badge, Button, Card, Input, Modal, Select, Spinner } from '@/components/ui';
import {
  createPolicy,
  listPolicies,
  POLICY_CATEGORY_LABELS,
  type PolicyCategory,
  type PolicyContext,
} from '@/api/services/hrPolicies.service';
import { useMe } from '@/features/settings/hooks/useMe';
import { useCompanyNavList } from '@/features/companies/hooks/useCompanyNavList';
import { useCurrentOrganization } from '@/features/organization/hooks/useCurrentOrganization';
import type { MeRoleId } from '@/types/api.types';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { HrCompanySelect, type HrAreaProps } from '../hrShared';
import { formatPolicyDate, POLICY_STATUS, POLICY_VERSION_STATUS } from './policyFormat';
import { PolicyDialog } from './PolicyDialog';

const CATEGORY_OPTIONS = (Object.keys(POLICY_CATEGORY_LABELS) as PolicyCategory[]).map(
  (value) => ({ value, label: POLICY_CATEGORY_LABELS[value] })
);

function policyTimeZone(value: string | undefined): string {
  if (!value) return 'UTC';
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: value });
    return value;
  } catch {
    return 'UTC';
  }
}

function useOrganizationWide(): boolean {
  const { data: me } = useMe();
  return useMemo(() => {
    if (!me?.roleId || typeof me.roleId === 'string') return false;
    return (me.roleId as MeRoleId).scope === 'all';
  }, [me]);
}

/** The whole organization, for those who may own a policy at that level. */
const WHOLE_ORGANIZATION = 'organization';

function CreatePolicyDialog({
  context,
  organizationWide,
  organizationTimeZone,
  timeZoneFor,
  onCreated,
  onClose,
}: {
  context: PolicyContext;
  organizationWide: boolean;
  organizationTimeZone: string;
  timeZoneFor: (companyId: string) => string;
  onCreated: (policy: { id: string; context: PolicyContext }) => void;
  onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const { data: companies } = useCompanyNavList();
  const [name, setName] = useState('');
  const [category, setCategory] = useState<PolicyCategory>('handbook');
  const [owner, setOwner] = useState(context.companyId ?? WHOLE_ORGANIZATION);
  const [error, setError] = useState<string | null>(null);
  const companyList = Array.isArray(companies) ? companies : [];
  const forOrganization = owner === WHOLE_ORGANIZATION;
  const createContext: PolicyContext = forOrganization
    ? { timeZone: organizationTimeZone }
    : { companyId: owner, timeZone: timeZoneFor(owner) };
  const create = useMutation({
    mutationFn: () =>
      createPolicy(createContext, {
        name: name.trim(),
        category,
        ...(forOrganization && {
          audience: {
            companyIds: companyList.map((row) => row._id),
          },
        }),
      }),
    onSuccess: ({ policy }) => {
      void queryClient.invalidateQueries({ queryKey: ['hr-policies'] });
      onCreated({ id: policy._id, context: createContext });
    },
    onError: (mutationError) => setError(getErrorMessage(mutationError)),
  });
  return (
    <Modal open onClose={onClose} title="New policy" className="max-w-md">
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim().length >= 2) create.mutate();
        }}
      >
        <Input
          id="policy-name"
          label="Policy name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Select
          clearable={false}
          label="Category"
          value={category}
          onChange={(value) => setCategory(value as PolicyCategory)}
          options={CATEGORY_OPTIONS}
        />
        {organizationWide && (
          <Select
            id="policy-owner"
            label="Applies to"
            clearable={false}
            value={owner}
            onChange={setOwner}
            options={[
              { value: WHOLE_ORGANIZATION, label: 'Every company' },
              ...companyList.map((company) => ({ value: company._id, label: company.name })),
            ]}
          />
        )}
        <p className="text-xs text-gray-600 dark:text-gray-300">
          {forOrganization
            ? 'Goes to every company unless you narrow its audience. '
            : 'Goes to this company. '}
          Write or upload the content next; another HR user approves it before it is published.
        </p>
        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={name.trim().length < 2}
            loading={create.isPending}
          >
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function PolicyList({
  context,
  organizationWide,
  organizationTimeZone,
  timeZoneFor,
  companyNameById,
  showCompany,
}: {
  context: PolicyContext;
  organizationWide: boolean;
  organizationTimeZone: string;
  timeZoneFor: (companyId: string) => string;
  companyNameById: Map<string, string>;
  showCompany: boolean;
}): React.ReactElement {
  const [creating, setCreating] = useState(false);
  useOpenFromQuery(setCreating, 'newPolicy');
  const [openId, setOpenId] = useState<string | null>(null);
  const [createdContext, setCreatedContext] = useState<PolicyContext | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['hr-policies', context.companyId ?? 'organization', showArchived],
    queryFn: () => listPolicies(context, showArchived),
  });
  const policies = Array.isArray(data) ? data : [];
  const selectedPolicy = policies.find((policy) => policy._id === openId);

  return (
    <Card
      header={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                checked={showArchived}
                onChange={(event) => setShowArchived(event.target.checked)}
              />
              Show archived
            </label>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="mr-1 h-4 w-4" aria-hidden />
              New policy
            </Button>
        </div>
      }
      contentClassName="p-0"
    >
      {isLoading ? (
        <div className="flex justify-center py-10" role="status">
          <Spinner />
          <span className="sr-only">Loading policies</span>
        </div>
      ) : error ? (
        <p className="p-4 text-sm text-danger" role="alert">
          {getErrorMessage(error)}
        </p>
      ) : policies.length === 0 ? (
        <p className="p-6 text-center text-sm text-gray-600 dark:text-gray-300">
          No policies yet. Create one to publish a handbook or policy for acknowledgement.
        </p>
      ) : (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {policies.map((policy) => {
            const open =
              policy.latest && policy.latest._id !== policy.current?._id ? policy.latest : null;
            return (
              <li
                key={policy._id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {policy.name}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    {POLICY_CATEGORY_LABELS[policy.category]}
                    {policy.ownerCompanyId
                      ? showCompany
                        ? ` · ${companyNameById.get(policy.ownerCompanyId) ?? 'Company'}`
                        : ''
                      : ' · Every company'}
                    {policy.current
                      ? ` · version ${policy.current.version} effective ${formatPolicyDate(policy.current.effectiveAt, policy.ownerCompanyId ? timeZoneFor(policy.ownerCompanyId) : organizationTimeZone)}`
                      : ' · not published yet'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {policy.status !== 'active' && (
                    <Badge variant={POLICY_STATUS[policy.status].variant}>
                      {POLICY_STATUS[policy.status].label}
                    </Badge>
                  )}
                  {open && (
                    <Badge variant={POLICY_VERSION_STATUS[open.status].variant}>
                      v{open.version}: {POLICY_VERSION_STATUS[open.status].label}
                    </Badge>
                  )}
                  <Button size="sm" variant="secondary" onClick={() => setOpenId(policy._id)}>
                    Open <span className="sr-only">{policy.name}</span>
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {creating && (
        <CreatePolicyDialog
          context={context}
          organizationWide={organizationWide}
          organizationTimeZone={organizationTimeZone}
          timeZoneFor={timeZoneFor}
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false);
            setCreatedContext(created.context);
            setOpenId(created.id);
          }}
        />
      )}
      {openId && (
        <PolicyDialog
          key={openId}
          context={
            createdContext ?? {
              ...context,
              timeZone: selectedPolicy?.ownerCompanyId
                ? timeZoneFor(selectedPolicy.ownerCompanyId)
                : selectedPolicy
                  ? organizationTimeZone
                  : context.timeZone,
            }
          }
          policyId={openId}
          onClose={() => {
            setOpenId(null);
            setCreatedContext(null);
          }}
        />
      )}
    </Card>
  );
}

/**
 * Policies and handbooks. Organization-wide HR sees all of them through the
 * organization route, narrowing to a company when the filter holds one. Company
 * HR works through their company's route, which needs exactly one company.
 */
export function HrPoliciesTab({
  companyIds,
  showCompany,
  companies,
}: HrAreaProps & {
  companies: Array<{ _id: string; name: string; settings?: { timezone?: string } }>;
}): React.ReactElement {
  const organizationWide = useOrganizationWide();
  const { data: organization } = useCurrentOrganization();
  const organizationTimeZone = policyTimeZone(organization?.settings?.timezone);
  const inScope = companyIds?.length
    ? companies.filter((company) => companyIds.includes(company._id))
    : companies;
  const [picked, setPicked] = useState('');
  const singleCompany = inScope.length === 1 ? inScope[0]._id : null;
  // Company HR with several companies picks one; everyone else follows the filter.
  const companyId = singleCompany ?? (organizationWide ? null : picked || inScope[0]?._id || null);
  const timeZoneFor = (id: string): string =>
    policyTimeZone(
      companies.find((company) => company._id === id)?.settings?.timezone ?? organizationTimeZone
    );
  const context: PolicyContext = companyId
    ? { companyId, timeZone: timeZoneFor(companyId) }
    : { timeZone: organizationTimeZone };
  const companyNameById = new Map(companies.map((company) => [company._id, company.name]));
  return (
    <div className="space-y-4">
      {!organizationWide && !singleCompany && (
        <HrCompanySelect
          id="policy-company"
          companies={inScope}
          value={companyId ?? ''}
          onChange={setPicked}
        />
      )}
      <PolicyList
        key={context.companyId ?? 'organization'}
        context={context}
        organizationWide={organizationWide}
        organizationTimeZone={organizationTimeZone}
        timeZoneFor={timeZoneFor}
        companyNameById={companyNameById}
        showCompany={showCompany && !companyId}
      />
    </div>
  );
}
